import { plurals } from './plural';

const { count, pick } = plurals('en');

/**
 * The interface's words in English, which every other catalog translates. Functions build
 * messages with counts or names; `{name}` placeholders are filled with elements (see rich.tsx).
 */
export const en = {
  meta: {
    title: 'Lyrmo — Free AI Text Rewriter & Grammar Checker',
  },
  app: {
    title: 'Polish your draft.',
    /** On the home page, over its title. */
    badge: 'AI-powered',
    description: 'Four versions, every change explained.',
  },
  promo: {
    watch: 'Watch overview',
    label: 'Studio overview',
    captions: 'Captions',
    failed: 'This video couldn’t play in your browser.',
    openVideo: 'Open video',
    play: 'Play',
    pause: 'Pause',
    replay: 'Replay',
    mute: 'Mute',
    unmute: 'Unmute',
    volume: 'Volume',
    seek: 'Video progress',
    loading: 'Loading video…',
  },
  shell: {
    tagline: 'A little clarity goes a long way.',
    aiNote: 'Rewrites are generated by AI. Review before sending.',
    privacy: 'Privacy',
    siteLanguage: 'Site language',
  },
  navigation: {
    label: 'Main navigation',
    write: 'Studio',
    desktop: 'Desktop apps',
    extension: 'Chrome extension',
    guide: 'How it works',
    home: 'Home',
    pricing: 'Pricing',
    /** The header's link to the desktop app for this computer, such as "Download for Mac". */
    download: (system: string) => `Download for ${system}`,
    skipToContent: 'Skip to content',
  },
  home: {
    /** {accent} is titleAccent, in the brand's colour. */
    title: 'We help good ideas find {accent}',
    titleAccent: 'better words.',
    description:
      'Lyrmo rewrites your text in four versions, from light grammar fixes to a fully polished draft. It shows you every change, so you choose what sounds like you.',
    start: 'Open the Studio',
    watch: 'Watch overview',
    /** Under each number in the hero, such as "15" over "tones to choose from". */
    stats: {
      versions: (n: number) =>
        pick(n, { one: 'version of each draft', other: 'versions of each draft' }),
      tones: (n: number) => pick(n, { one: 'tone to choose from', other: 'tones to choose from' }),
      languages: (n: number) =>
        pick(n, { one: 'language to write in', other: 'languages to write in' }),
    },
    featuresTitle: 'What Lyrmo does',
    featuresDescription: 'Take a closer look. Each feature has its own short demo.',
    features: {
      rewrite: {
        label: 'Rewrite',
        title: 'See every change, and why.',
        /** {diff}, {why}, and {alts} are the version card's views, named as on the card. */
        body: '{diff} marks each edit, {why} explains it, and {alts} offers other ways to say it.',
      },
      tones: {
        label: 'Tone',
        title: 'Find the right voice.',
        body: 'Choose a built-in tone or create your own, with preferred terms and rules for how you write.',
      },
      instructions: {
        label: 'Instruction',
        title: 'Tell it what matters.',
        body: 'Add instructions for one rewrite, or save them and combine them whenever you need.',
      },
      tools: {
        label: 'AI tools',
        title: 'Reshape it in one click.',
        body: 'Bullet points, a checklist, a summary, or tools of your own, for the whole draft or just the part you select.',
      },
      dictation: {
        label: 'Dictation',
        title: 'Say it. Keep your flow.',
        body: 'Speak your draft instead of typing. Reduce background noise and turn your words into text.',
      },
      'image-text': {
        label: 'Image text',
        title: 'Pictures into words.',
        body: 'Import, paste, or drop a screenshot or photo. Read its text exactly as written, or describe what it shows, then rewrite it like any draft.',
      },
    },
    desktopTitle: 'Rewrite in any app.',
    desktopDescription:
      'Select text anywhere on your computer, press one shortcut, and choose a version. Lyrmo puts it in place.',
    extensionTitle: 'Rewrite on any web page.',
    extensionDescription:
      'Select text on any page in Chrome, press one shortcut, and choose a version. Lyrmo puts it in place.',
    values: {
      title: 'What we care about',
      authorTitle: 'You stay the author.',
      authorBody: 'Lyrmo suggests and explains. You decide what to keep.',
      freeTitle: 'Free to start.',
      freeBody: 'Free credits every day, and a plan for when you write more.',
    },
    closingTitle: 'Ready when your words are.',
    closingDescription: 'Bring a draft to the Studio, or take a closer look at each feature.',
  },
  pricing: {
    title: 'Start free. Upgrade when you write more.',
  },
  cookies: {
    label: 'Cookies',
    message:
      'Lyrmo uses necessary cookies to keep you signed in and the site working. With your permission, we also use analytics cookies to see which features people use, so we can improve them. You can change your choice anytime in Cookie settings.',
    learnMore: 'Learn more',
    allow: 'Allow',
    decline: 'Decline',
    settings: 'Cookie settings',
  },
  downloads: {
    link: 'Download desktop app',
    heading: 'Lyrmo for your computer',
    description: 'Use Lyrmo in other apps with a keyboard shortcut.',
    download: 'Download',
    unavailable: 'Not available yet',
    loading: 'Checking downloads…',
    failed: 'Downloads are temporarily unavailable.',
    started: 'Download started',
    setup: 'Get set up',
    macInstall: 'Open the .dmg and drag Lyrmo to Applications.',
    macAccess: 'Open Lyrmo and allow Accessibility access when asked.',
    windowsInstall: 'Run the installer and choose where to install.',
    windowsOpen: 'Open Lyrmo. It waits in the system tray.',
    linuxInstall: 'Make the AppImage executable, then open it.',
    linuxSession: 'Use an X11 session. Wayland isn’t supported.',
    /** {keys} is the shortcut, drawn as keys. */
    shortcut: 'Select text in any app and press {keys}.',
    copyCommand: 'Copy command',
    copied: 'Copied',
    /** The link to the extension in the Chrome Web Store, in the store's own words. */
    addToChrome: 'Add to Chrome',
  },
  common: {
    close: 'Close',
    cancel: 'Cancel',
    back: 'Back',
    done: 'Done',
    retry: 'Retry',
    tryAgain: 'Try again',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signOut: 'Sign out',
    signingOut: 'Signing out…',
    manageBilling: 'Manage billing',
    notNow: 'Not now',
    /** {email} is the address, in bold. */
    signedInAs: 'Signed in as {email}',
  },
  counts: {
    words: (n: number) => count(n, { one: '# word', other: '# words' }),
    images: (n: number) => count(n, { one: '# image', other: '# images' }),
    characters: (n: number) => count(n, { one: '# character', other: '# characters' }),
  },
  time: {
    tomorrow: (time: string) => `${time} tomorrow`,
  },
  planNames: { free: 'Free', plus: 'Plus', pro: 'Pro', max: 'Max' },
  options: {
    group: 'Rewrite options',
    tone: 'Tone',
    length: 'Length',
    language: 'Language',
    tones: {
      natural: 'As written',
      friendly: 'Friendly',
      casual: 'Casual',
      formal: 'Formal',
      confident: 'Confident',
      empathetic: 'Empathetic',
      persuasive: 'Persuasive',
      diplomatic: 'Diplomatic',
      direct: 'Direct',
      polite: 'Polite',
      enthusiastic: 'Enthusiastic',
      inspiring: 'Inspiring',
      playful: 'Playful',
      academic: 'Academic',
      objective: 'Objective',
    },
    /** Heads the writer's own tones in the tone menu. */
    yourTones: 'Your tones',
    /** Names a chosen custom tone while the writer's tones load. */
    customTone: 'Custom tone',
    lengths: { same: 'Same length', shorter: 'Shorter', longer: 'Longer' },
    sameLanguage: 'Same as my text',
    /** Under "Same as my text" in the language menu. */
    sameLanguageHint: 'Each version in your text’s own language',
    /** Heads the languages a rewrite can be written in, in the language menu. */
    languages: 'Languages',
    /** Under each length in the length menu. */
    lengthHints: {
      same: 'About as long as your text',
      shorter: 'Tighter, keeping what matters',
      longer: 'Fuller, with more detail',
    },
    /** Puts tone, length, language, and instructions back as they first were. */
    reset: 'Reset',
    resetTitle: 'Reset the options to their defaults',
    instruction: 'Instruction',
    instructionLabel: 'Instruction for the rewrites',
    instructionPlaceholder: 'For example: make it persuasive for a client',
    removeInstruction: 'Remove the instruction',
    /** Heads the instruction typed for one rewrite, in the instruction menu. */
    justThisOnce: 'Just for this rewrite',
    /** Saves the instruction typed for one rewrite, to choose again later. */
    saveForLater: 'Save for later',
    savedInstructions: 'Saved instructions',
    savedLoading: 'Loading your instructions…',
    noSaved: 'Save instructions you use often to choose them here, several at once.',
    manageInstructions: 'Manage instructions',
    /** Names a chosen saved instruction while the writer's instructions load. */
    savedInstruction: 'Saved instruction',
    /** Under the saved instructions, once as many are chosen as one rewrite follows. */
    atMost: (max: number) => `Up to ${String(max)} at once.`,
    stopFollowing: (name: string) => `Stop following ${name}`,
    /** Names the chips of the instructions the rewrites follow. */
    activeInstructions: 'Instructions for the rewrites',
  },
  editor: {
    heading: 'Your writing',
    expand: 'Full screen',
    collapse: 'Exit full screen',
    /** The studio's editor with the whole window to itself, the header and footer set aside. */
    focus: 'Focus mode',
    leaveFocus: 'Leave focus mode',
    clear: 'Clear',
    /** The draft and its rewrites together; Clear (the trash) takes the draft alone. */
    clearAll: 'Clear all',
    clearAllTitle: 'Clear your text and its rewrites',
    /** The button that opens the keyboard shortcuts. */
    shortcuts: 'Shortcuts',
    textLabel: 'Text to rewrite',
    placeholder: 'Paste your text here, or start writing…',
    /** Read out by screen readers when word completion offers the rest of a word. */
    suggestion: (word: string) => `Suggestion: ${word}. Press Tab to use it.`,
    /** The list of words that start with the letters typed, and its switch. */
    wordList: 'Word list',
    wordListHint: 'List matching words to pick with the arrow keys and Tab, or a click.',
    /** The menu of switches for the helpers below, which work on English text. */
    writingAids: 'Spelling',
    writingAidsNote: 'These work while you write in English.',
    /** Word completion: the rest of the word being typed, in grey after the caret. */
    completeWords: 'Complete words',
    completeWordsHint: 'Finish the word you’re typing in grey. Tab accepts it.',
    /** Spell check: words that look mistyped, underlined. */
    spellCheck: 'Spell check',
    spellCheckHint: 'Underline words that look mistyped. Click one for fixes.',
    /** Spell check: the words a word that looks mistyped could be, listed when it's clicked. */
    spelling: 'Spelling suggestions',
    /** The last choice in that list: leave the word alone from now on, in this browser. */
    addToDictionary: 'Add to dictionary',
    removeCharacters: (n: number) =>
      `Remove ${count(n, { one: '# character', other: '# characters' })} to rewrite this draft.`,
    tools: 'Draft tools',
    undo: 'Undo',
    undoTitle: (keys: string) => `Undo (${keys})`,
    redo: 'Redo',
    redoTitle: (keys: string) => `Redo (${keys})`,
    importFile: 'Import text or image',
    importTitle: 'Import a text, Markdown, HTML, Word, or image file',
    imageTextProgress: 'Reading image text…',
    describingImage: 'Describing image…',
    imageTextHint: (credits: string) =>
      `Images · read their text or describe them, ${credits} each. PNG, JPEG, or WebP up to 10 MB. Images are sent to OpenAI.`,
    /** Asked once images come in, before they're sent: what to do with them. */
    imageChoice: 'Read the image’s text, or describe it',
    readText: 'Read text',
    describeImage: 'Describe',
    /** An image had no text to read: just that; Read text is turned off, and Describe stays. */
    noTextFound: 'No text found.',
    /** Over the draft while writing or files from another app are dragged over the page. */
    dropHere: 'Drop to add to your draft',
    fileLabel: 'Text or image file',
    paste: 'Paste',
    pasteTitle: (keys: string) => `Paste (${keys})`,
    copy: 'Copy',
    copyTitle: 'Copy draft',
    /** Reads the draft aloud in an AI voice, as the versions can be. */
    readAloud: 'Read draft aloud',
    stopReading: 'Stop reading draft',
    readAloudFailed: 'Couldn’t play audio. Select Read draft aloud to try again.',
    /** After the character count, such as "14 / 5,000 characters"; hidden on narrow screens. */
    charactersUnit: 'characters',
    creditsLeftToday: (n: number) =>
      n === 0
        ? 'no credits left today'
        : count(n, { one: '# credit left today', other: '# credits left today' }),
    rewrite: 'Rewrite',
    wait: (time: string) => `Wait ${time}`,
    feedback: {
      copied: 'Draft copied.',
      copyFailed: 'Couldn’t copy. Select your text and use your keyboard to copy it.',
      wrongFileType: 'Choose a plain text (.txt) or Markdown (.md) file.',
      fileTooLarge: 'Choose a text file smaller than 256 KB.',
      /** Importing into the draft, which, unlike opening a draft, takes web pages and Word too. */
      wrongImportType: 'Choose a text, Markdown, HTML, Word (.docx), PNG, JPEG, or WebP file.',
      importTooLarge: 'This file is too large to import.',
      importChanged: 'Your draft changed. Import the file again.',
      unreadableFile: 'This file has no readable text.',
      imported: (name: string) => `Imported ${name}. You can undo this change.`,
      importFailed: 'Couldn’t read this file. Try another file.',
      pasteChanged: 'Your draft changed. Try pasting again.',
      clipboardEmpty: 'Your clipboard has no text to paste.',
      pasted: 'Text pasted.',
      pasteUnavailable: (keys: string) =>
        `Paste isn’t available here. Click the editor and press ${keys}.`,
    },
  },
  /**
   * Saving the draft, to open it again later: to the writer's account, or as a Markdown file on
   * this computer. Nothing is saved unless they choose to.
   */
  drafts: {
    /** The button over the draft that opens the Drafts dialog. */
    button: 'Drafts',
    buttonTitle: 'Save this draft, or open another',
    title: 'Drafts',
    saveHeading: 'Save this draft',
    name: 'Name',
    /** When the draft gives no name to suggest, and for files with none. */
    untitled: 'Untitled draft',
    toAccount: 'Save to account',
    toComputer: 'Save to this computer',
    /** For a draft opened from, or saved to, the account. */
    saveChanges: 'Save changes',
    saveCopy: 'Save as new draft',
    saving: 'Saving…',
    nothingToSave: 'Write something first, then save it here.',
    /** {max} is the number of characters, formatted. */
    tooLong: (max: string) => `A draft in your account can have up to ${max} characters.`,
    /** Where the browser lets the writer choose a folder, and where it downloads. */
    computerNote: 'Saves a Markdown (.md) file that keeps your formatting.',
    downloadNote: 'Downloads a Markdown (.md) file that keeps your formatting.',
    accountPitch: 'Sign in to keep drafts in your account, and open them on any device.',
    openHeading: 'Open a draft',
    openFile: 'Open from this computer',
    fileLabel: 'Draft file',
    inAccount: 'In your account',
    loading: 'Loading your drafts…',
    empty: 'No drafts in your account yet.',
    find: 'Find a draft',
    noMatch: 'No drafts match.',
    /** {when} is a time when `today`, else a day. */
    savedAt: (when: string, today: boolean) => (today ? `Saved at ${when}` : `Saved ${when}`),
    /** Marks the draft in the list that's in the editor now. */
    current: 'Open now',
    open: (name: string) => `Open ${name}`,
    delete: 'Delete',
    deleteDraft: (name: string) => `Delete ${name}`,
    confirmDelete: (name: string) => `Delete ${name}? This can’t be undone.`,
    limit: (max: number) =>
      `An account can keep ${String(max)} drafts. Delete one to save another.`,
    /** Next to the heading over the draft: what it's saved as, and whether it has changed since. */
    inAccountTitle: (name: string) => `${name}, in your account`,
    onComputerTitle: (name: string) => `${name}, on this computer`,
    saved: 'Saved',
    unsaved: 'Unsaved changes',
    save: 'Save',
    saveTitle: (keys: string) => `Save changes (${keys})`,
    savedToAccount: (name: string) => `Saved ${name} to your account.`,
    savedToComputer: (name: string) => `Saved ${name}.`,
    downloaded: (name: string) => `Downloaded ${name}.`,
    opened: (name: string) => `Opened ${name}.`,
    saveFailed: 'Couldn’t save the file. Try again, or choose another place.',
    openFailed: 'Couldn’t open this file. Try another one.',
  },
  /** The formatting toolbar over the draft, and the formatting keys. */
  format: {
    toolbar: 'Formatting',
    /** The menu of what the line at the caret is: text or a heading. */
    textStyle: 'Text style',
    styles: {
      paragraph: 'Normal text',
      heading1: 'Heading 1',
      heading2: 'Heading 2',
      heading3: 'Heading 3',
      heading4: 'Heading 4',
      heading5: 'Heading 5',
      heading6: 'Heading 6',
      quote: 'Quote',
      code: 'Code block',
    },
    bold: 'Bold',
    italic: 'Italic',
    /** The menu of the rest of what words can be, as Jira's ··· has it. */
    more: 'More formatting',
    underline: 'Underline',
    strike: 'Strikethrough',
    code: 'Code',
    subscript: 'Subscript',
    superscript: 'Superscript',
    /** A line across the draft, between parts of it. */
    divider: 'Divider',
    link: 'Link',
    linkAddress: 'Link address',
    linkPlaceholder: 'Paste or type a link',
    applyLink: 'Apply',
    removeLink: 'Remove link',
    badLink: 'Enter a web or email address, such as example.com.',
    bullets: 'Bulleted list',
    numbered: 'Numbered list',
    checklist: 'Checklist',
    clear: 'Clear formatting',
    /** The menu of the colors words can be in, and highlighted with, as Jira's text color has it. */
    color: 'Text color',
    highlight: 'Highlight',
    noHighlight: 'No highlight',
    colors: {
      default: 'Default',
      gray: 'Gray',
      red: 'Red',
      orange: 'Orange',
      yellow: 'Yellow',
      green: 'Green',
      blue: 'Blue',
      purple: 'Purple',
    },
    /** Puts a table in; in one, the menu of what to do to it. */
    table: 'Table',
    tableActions: {
      addRow: 'Add row',
      addColumn: 'Add column',
      deleteRow: 'Delete row',
      deleteColumn: 'Delete column',
      deleteTable: 'Delete table',
    },
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    intro: 'Choose a shortcut, then press the keys you’d like instead. Esc keeps the one you have.',
    actions: {
      rewrite: 'Rewrite',
      useVersion: 'Copy version 1–4',
      clearDraft: 'Clear the draft',
      fullScreen: 'Focus mode',
      showShortcuts: 'Show keyboard shortcuts',
    },
    /** The button that records new keys: the action, and its keys now. */
    change: (action: string, keys: string) => `${action}: ${keys}. Change`,
    press: (action: string) => `Press the new keys for ${action}`,
    recording: 'Press keys…',
    needsNumber: 'Hold your keys with a number, such as 1.',
    reset: 'Reset',
    resetOne: (action: string) => `Reset ${action} to its default`,
    resetAll: 'Reset all',
    /** Heads the shortcuts that can’t be changed. */
    always: 'Always',
    fixed: {
      close: 'Close menus and suggestions',
      undo: 'Undo',
      redo: 'Redo',
      complete: 'Take the suggested word',
      save: 'Save the draft',
    },
    /** Why keys can’t be used; {modifiers} lists the keys to hold, such as “Ctrl, Alt”. */
    conflict: {
      modifier: (keys: string, modifiers: string) =>
        `Hold one of ${modifiers} with the key, so typing never sets ${keys} off.`,
      character: (keys: string) => `${keys} types a character on some keyboards.`,
      standard: (keys: string) => `${keys} is a standard shortcut, such as copy, paste, or undo.`,
      browser: (keys: string) => `${keys} is a browser shortcut, such as for switching tabs.`,
      system: (keys: string) => `${keys} is used by the system.`,
      apps: (keys: string) => `${keys} is used by other apps.`,
      taken: (keys: string, action: string) => `${keys} is already the shortcut for ${action}.`,
    },
  },
  tiers: {
    minorGrammarFixes: {
      label: 'Minor grammar fixes',
      description: 'Spelling, punctuation, and grammar. Just the essentials.',
    },
    naturalRewording: {
      label: 'More natural',
      description: 'The same meaning, with a more fluent flow.',
    },
    enhancedClarity: {
      label: 'Enhanced clarity',
      description: 'Tighter structure. A clearer train of thought.',
    },
    optimalVersion: {
      label: 'Optimal version',
      description: 'Your ideas, in their strongest form.',
    },
  },
  empty: {
    title: 'Find your better words.',
    description: 'Add your text and explore different ways to say it.',
    preview: 'Versions you’ll get',
  },
  rateLimit: {
    title: 'Take a short breather.',
    reached: 'You’ve reached the rewrite limit.',
    /** {time} is the countdown. */
    tryAgainIn: 'You can try again in {time}.',
    now: 'You can rewrite again now.',
  },
  creditLimit: {
    notEnough: 'Not enough credits left today.',
    usedUp: 'You’ve used today’s credits.',
    /** {time} is when the allowance resets. */
    resetsAt: 'Your credits reset at {time}.',
    getMore: 'Get more credits',
  },
  results: {
    heading: 'Rewrites',
    rewriting: 'Rewriting…',
    downloadAll: 'Download all',
    downloadAllTitle: 'Download your original and every version as a text file',
    /** Clears the versions, leaving the draft as it is. */
    clear: 'Clear',
    clearTitle: 'Clear the rewrites',
    /** After a version replaces the draft; its toast offers Undo. */
    nowEditing: (label: string) => `${label} is your text now.`,
    /** The heading over the original in a downloaded file, and its file name. */
    original: 'Original',
    fileName: 'rewrites',
    announce: {
      rateLimited: 'Rewrite limit reached. Please wait before trying again.',
      rateLimitedFor: (time: string) => `Rewrite limit reached. You can try again in ${time}.`,
      creditLimit: (message: string) => `Daily credit limit reached. ${message}`,
      failed: 'The rewrites couldn’t be generated. Use Retry to try again.',
      ready: (n: number) =>
        `${String(n)} rewrites ready. Each version can be copied or compared with your original.`,
      partlyReady: (ready: number, failed: number) =>
        `${String(ready)} rewrites ready. ${String(failed)} could not be generated. Use Retry to try again.`,
    },
  },
  speech: {
    speak: (label: string) => 'Speak ' + label,
    stop: (label: string) => 'Stop speaking ' + label,
    loading: 'Preparing speech…',
    aiVoice: 'AI-generated voice',
    failed: 'Couldn’t play audio. Select Speak to try again.',
  },
  card: {
    failed: 'This version couldn’t be generated. Please try again.',
    download: (label: string) => `Download ${label}`,
    downloadTitle: (label: string) => `Download ${label} as a text file`,
    edit: (label: string) => `Use ${label} as your text`,
    editTitle: (label: string) => `Use ${label} as your text, to edit it or rewrite it again`,
    copy: (label: string) => `Copy ${label}`,
    copied: (label: string) => `${label} copied`,
    copyFailed: (label: string) => `Copy failed. Retry copying ${label}`,
    views: (label: string) => `View ${label}`,
    text: 'Text',
    diff: 'Diff',
    why: 'Why',
    alts: 'Alts',
    compared: 'Compared with original',
    whyEachChange: 'Why each change helps',
    otherWays: 'Other ways to say each change',
  },
  alts: {
    /** Marks the writer's own words, among other ways to say a change. */
    yours: 'Yours',
    choices: (change: number) => `Ways to say change ${String(change)}`,
  },
  explain: {
    became: 'became',
    unchanged: 'This version keeps your text as it was.',
    kinds: {
      grammar: 'Grammar',
      spelling: 'Spelling',
      punctuation: 'Punctuation',
      'word-choice': 'Word choice',
      clarity: 'Clarity',
      concision: 'Concision',
      tone: 'Tone',
      structure: 'Structure',
      style: 'Style',
      translation: 'Translation',
      other: 'Other',
    },
  },
  diff: {
    added: ' [added] ',
    deleted: ' [deleted] ',
  },
  account: {
    label: 'Account',
    guest: 'Guest',
    savedInBrowser: 'Credits saved in this browser',
    todaysCredits: 'Today’s credits',
    creditsUsed: 'Credits used today',
    creditsLeft: 'Credits left today',
    usedLeft: (used: string, left: string) => `${used} used · ${left} left`,
    resetsAt: (time: string) => `Resets at ${time}`,
    upgrade: 'Upgrade',
    upgradeForMore: 'Upgrade for more credits',
    changePlan: 'Change plan',
    billing: 'Billing',
    paymentOverdue: 'Payment overdue',
    ends: (date: string) => `Ends ${date}`,
    renews: (date: string) => `Renews ${date}`,
    chipTitle: (used: string, limit: string, time: string) =>
      `${used} of ${limit} credits used today. Resets at ${time}.`,
    /**
     * The header's credits left: {count} is the number, {unit} and {when} the credits hidden on
     * narrow screens.
     */
    chipLeft: '{count}{unit} left{when}',
    chipUnit: (n: number) => pick(n, { one: ' credit', other: ' credits' }),
    chipWhen: ' today',
    /** Heads what today's credits went on, in the header's credits breakdown. */
    spentOn: 'Spent today on',
    /** Credits that went on nothing listed: before they were itemized, or by others on the network. */
    otherUse: 'Other',
    /** Heads what the credits left would pay for, such as "What 80 credits buy". */
    creditsBuy: (n: number) => count(n, { one: 'What # credit buys', other: 'What # credits buy' }),
  },
  plans: {
    title: 'Choose your plan',
    signInTitle: 'Sign in to subscribe',
    signInIntro: (plan: string, yearly: boolean) =>
      `Enter your email and we’ll send you a sign-in link and code. Once you’re signed in, you’ll continue to checkout for ${plan}${yearly ? ', billed yearly' : ''}.`,
    intro: 'Use your credits for text, dictation, or speech. Credits reset daily at midnight UTC.',
    loading: 'Loading plans…',
    actionsUnavailable: 'Plan upgrades and changes are temporarily unavailable.',
    billingPeriod: 'Billing period',
    monthly: 'Monthly',
    yearly: 'Yearly',
    monthsFree: (n: number) => count(n, { one: '# month free', other: '# months free' }),
    /** Read out for the crossed-out price over a yearly one: twelve months at the monthly price. */
    billedMonthly: (price: string) => `${price} a year, billed monthly`,
    current: 'Current plan',
    cancelAnytime: 'Cancel anytime in Manage billing.',
    switchTo: (plan: string) => `Switch to ${plan}`,
    choose: (plan: string) => `Choose ${plan}`,
    free: 'Free',
    per: { day: 'day', week: 'week', month: 'month', year: 'year' },
    /** {count} is the number of credits, in bold. */
    creditsADay: (n: number) =>
      pick(n, { one: '{count} credit a day', other: '{count} credits a day' }),
    pastDue: 'Your last payment failed. Update your card in Manage billing.',
    endsOn: (date: string) => `Ends on ${date}.`,
    renewsOn: (date: string) => `Renews on ${date}.`,
    switchToYearly: 'Switch to yearly billing',
    switchToMonthly: 'Switch to monthly billing',
    changeTitle: 'Confirm your new plan',
    changeTo: (plan: string, yearly: boolean) => `${plan}, billed ${yearly ? 'yearly' : 'monthly'}`,
    chargeNow: (amount: string) =>
      `You’ll be charged ${amount} now for the rest of this billing period.`,
    creditNow: (amount: string) => `You’ll get ${amount} in credit toward your next bills.`,
    noChargeNow: 'There’s nothing to pay now.',
    changeNote: 'Your new plan starts right away.',
    previewing: 'Working out the price…',
    confirmChange: 'Confirm change',
    changing: 'Changing plan…',
    alreadySubscribed: 'Already subscribed?',
    footnote:
      'Subscriptions renew monthly until cancelled. Payments are handled securely by Paddle.',
  },
  billing: {
    title: 'Manage billing',
    intro: 'Your plan, usage, and payment settings.',
    pastDueWarning:
      'Your last payment didn’t go through. Update your payment method to keep your plan.',
    subscription: 'Current subscription',
    currentPlan: 'Current plan',
    status: { pastDue: 'Payment overdue', ending: 'Ending soon', trial: 'Trial', active: 'Active' },
    /** {credits} is in bold. */
    summary: (n: number) =>
      pick(n, {
        one: '{credits} credit a day · All four rewrites',
        other: '{credits} credits a day · All four rewrites',
      }),
    /** {date} is in bold. */
    accessUntil: 'Access until {date}',
    trialEnds: 'Trial ends {date}',
    periodEnds: 'Current period ends {date}',
    renewsOn: 'Renews on {date}',
    comparePlans: 'Compare plans',
    endingNote: 'Your subscription won’t renew. You’ll move to Free when this period ends.',
    todaysCredits: 'Today’s credits',
    /** {left} is in bold. */
    leftOf: '{left} / {limit} left',
    resetsMidnight: 'Resets at midnight UTC',
    payments: 'Payments & subscription',
    paymentsText: 'Paddle, our payment provider, keeps your payment details. We never see them.',
    updatePayment: 'Update payment method',
    cancel: 'Cancel subscription',
    /** `date` is when the paid-for period ends, when that's known. */
    cancelQuestion: (plan: string, date: string | null) =>
      date
        ? `Cancel your ${plan} subscription? You keep ${plan} until ${date}, then move to Free.`
        : `Cancel your ${plan} subscription? You keep ${plan} until the end of this billing period, then move to Free.`,
    keep: 'Keep subscription',
    cancelling: 'Cancelling…',
    resuming: 'Keeping subscription…',
    history: 'Payment history',
    loadingPayments: 'Loading payments…',
    noPayments: 'No payments yet.',
    invoice: 'Invoice',
    invoiceFor: (date: string) => `Invoice for ${date}`,
  },
  signIn: {
    title: 'Sign in',
    intro: 'We’ll email you a link and a code to sign in.',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    continue: 'Continue',
    sending: 'Sending…',
    /** {policy} is the link to the privacy policy. */
    agree: 'By continuing, you agree to the {policy}.',
    policy: 'Privacy Policy',
    checkInbox: 'Check your inbox.',
    /** {title} is "Check your inbox." in bold; {email} the address. */
    sent: '{title} We sent a sign-in link and code to {email}. Open the link on this device, or enter the code here. They expire in 15 minutes.',
    code: 'Sign-in code',
    differentEmail: 'Use a different email',
    signedIn: 'You’re signed in.',
    signedInCheckout: 'You’re signed in. Taking you to checkout…',
  },
  notices: {
    subscribed: 'Thanks for subscribing! Your new daily credit allowance is active.',
    checkoutCancelled: 'Checkout was cancelled, so your plan hasn’t changed.',
    billingUpdated: 'Your billing details are up to date.',
    linkExpired: 'That sign-in link has expired or was already used.',
    getNewLink: 'Get a new link',
    signInToSubscribe: 'Sign in to subscribe.',
    planChanged: (plan: string) => `You’re now on ${plan}.`,
    cancelled:
      'Your subscription won’t renew. You keep your plan until the end of this billing period.',
    resumed: 'Your subscription will renew as usual.',
  },
  /** What the AI tools, tone, and instruction menus, and the dialogs that manage them, share. */
  menus: {
    /** Finds a choice by its name or what it does. */
    search: 'Search',
    noMatch: (query: string) => `Nothing matches “${query}”.`,
    /** A menu's first section: the choices the writer pinned. */
    pinned: 'Pinned',
    builtIn: 'Built-in',
    hidden: 'Hidden from the menu',
    /** Under a menu, when the writer hid some of its choices. */
    hiddenCount: (n: number) =>
      count(n, { one: '# is hidden from this menu.', other: '# are hidden from this menu.' }),
    /** How many of the choices a menu lists, such as "9 of 11 in the menu". */
    inMenu: (shown: number, total: number) => `${String(shown)} of ${String(total)} in the menu`,
    /** How many of their own the writer has made, of how many they can. */
    ownCount: (n: number, max: number) => `${String(n)} of ${String(max)}`,
    builtInBadge: 'Built-in, kept up to date by Lyrmo',
    ownBadge: 'Yours',
    builtInNote: 'Built-in ones can’t be edited. Duplicate one to change how it works.',
    duplicateBuiltIn: 'Duplicate and customize',
    edit: 'Edit',
    editItem: (name: string) => `Edit ${name}`,
    duplicateItem: (name: string) => `Duplicate ${name}`,
    deleteItem: (name: string) => `Delete ${name}`,
    delete: 'Delete',
    /** A duplicate's name, until the writer gives it another. */
    copyName: (name: string) => `${name} (copy)`,
    showInMenu: 'Show in the menu',
    showInMenuHint: 'Hidden ones stay here, ready to switch back on.',
    pin: 'Pin to the top',
    pinHint: 'Pinned ones come first in the menu.',
    /** Runs a choice on a sample, to see what it does before using it on a draft. */
    tryIt: 'Try it',
    tryIntro: 'Run it on a sample to see what it does.',
    before: 'Before',
    after: 'After',
    sample:
      'We met with the design team on Tuesday. We agreed to ship the new onboarding next sprint. Sam will update the copy. Priya will handle the analytics events. We still need a decision on pricing.',
    /** The button that tries a choice, with what that costs, such as "Try it · 70 credits". */
    run: (cost: string) => `Try it · ${cost}`,
    trying: 'Trying it…',
  },
  tones: {
    title: 'Manage tones',
    loading: 'Loading your tones…',
    empty: 'No tones yet. Make one for each kind of writing you do, such as support replies.',
    newTone: 'New tone',
    confirmDelete: (name: string) =>
      `Delete ${name}? Rewrites where it’s chosen go back to writing as written.`,
    saved: (name: string) => `${name} is saved. Choose it from Tone to rewrite in it.`,
    /** Stands in for a tone's description when it has none. */
    rules: (n: number) => count(n, { one: '# rule', other: '# rules' }),
    noRules: 'No rules yet',
    /** In the tone menu, under Your tones, before the writer has any. */
    noneYet: 'None yet. Make one for a voice you write in often.',
    /** Stands in for a tone's description when it has none. */
    noDescription: 'No description: its name says how it sounds.',
    rulesTitle: 'Rules',
    /** How each built-in tone sounds; a duplicate starts with it as its description. */
    about: {
      natural:
        'Keeps the tone the text already has: the versions fix and clarify it without changing how it sounds.',
      friendly: 'Warm and approachable.',
      casual: 'Relaxed and conversational.',
      formal: 'Formal and professional.',
      confident: 'Assured and direct, without hedging.',
      empathetic: 'Considerate and understanding.',
      persuasive: 'Persuasive, making a compelling case.',
      diplomatic: 'Tactful, softening criticism without losing the point.',
      direct: 'Plain and to the point, without pleasantries.',
      polite: 'Polite and courteous, with respectful requests.',
      enthusiastic: 'Upbeat and excited, without exaggerating.',
      inspiring: 'Uplifting, moving readers to act.',
      playful: 'Lighthearted, with a touch of humor.',
      academic: 'Precise and measured, for scholarly writing.',
      objective: 'Neutral, stating facts without opinion.',
    },
    /** Why As written can't be hidden. */
    defaultAlwaysShown: 'As written is the default, so it’s always in the menu.',
    limit: (max: number) => `You can have up to ${String(max)} tones. Delete one to make another.`,
    name: 'Name',
    namePlaceholder: 'Support replies',
    nameMissing: 'Give the tone a name.',
    nameTaken: (name: string) => `You already have a tone called ${name}.`,
    description: 'How it sounds',
    descriptionPlaceholder: 'Warm and brief, like a helpful colleague',
    terms: 'Preferred terms',
    term: (n: number) => `Preferred term ${String(n)}`,
    termPlaceholder: 'sign in',
    insteadOf: 'instead of',
    alternativesPlaceholder: 'log in, login',
    /** How a term is named when it's still empty. */
    termN: (n: number) => `term ${String(n)}`,
    replaces: (term: string) => `What ${term} replaces`,
    remove: (term: string) => `Remove ${term}`,
    addTerm: 'Add a term',
    avoid: 'Words to avoid',
    avoidPlaceholder: 'utilize, leverage, synergy',
    separate: 'Separate them with commas.',
    notes: 'Other rules',
    notesPlaceholder: 'Use the Oxford comma. Write numbers under ten as words.',
    save: 'Save tone',
    saving: 'Saving…',
    pitch:
      'Make tones of your own, each with its preferred terms, words to avoid, and house rules, and rewrite in them here and in the desktop app. Sign in to create yours.',
    termTooLong: (max: number) => `Keep each term to ${String(max)} characters or fewer.`,
    tooManyAlternatives: (max: number) =>
      `List up to ${String(max)} alternatives for each preferred term.`,
    tooManyAvoid: (max: number) => `List up to ${String(max)} words to avoid.`,
  },
  instructions: {
    title: 'Manage instructions',
    loading: 'Loading your instructions…',
    empty:
      'No instructions yet. Save one for each thing you often ask for, such as an email layout.',
    newInstruction: 'New instruction',
    confirmDelete: (name: string) => `Delete ${name}? Rewrites stop following it.`,
    saved: (name: string) => `${name} is saved. Choose it from Instruction to follow it.`,
    limit: (max: number) => `You can save up to ${String(max)}. Delete one to save another.`,
    name: 'Name',
    namePlaceholder: 'Email layout',
    nameMissing: 'Give the instruction a name.',
    nameTaken: (name: string) => `You already have an instruction called ${name}.`,
    text: 'What rewrites should do',
    textPlaceholder: 'Start with a greeting, keep paragraphs short, and end with a sign-off.',
    textMissing: 'Say what rewrites should do.',
    save: 'Save instruction',
    saving: 'Saving…',
    pitch:
      'Save instructions you use often, such as how to lay out an email, and choose any of them for a rewrite, several at once. Sign in to save yours.',
  },
  aiTools: {
    /** The editor's button, and its menu of tools that format the draft with AI. */
    button: 'AI tools',
    /** What running a tool on the draft costs, such as "This uses 84 credits." */
    cost: (amount: string) => `This uses ${amount}.`,
    /** Atop the menu: what a tool would format, with its words, such as "Whole draft · 35 words". */
    scopeSelection: (words: string) => `Selected text · ${words}`,
    scopeDraft: (words: string) => `Whole draft · ${words}`,
    builtIn: 'Formatting',
    tools: {
      bullets: { name: 'Bullet points', hint: 'Turn it into a bulleted list.' },
      numbered: { name: 'Numbered steps', hint: 'Lay it out as steps, in order.' },
      checklist: { name: 'Checklist', hint: 'Turn it into tasks to tick off.' },
      gherkin: { name: 'Gherkin scenarios', hint: 'Write it as Given, When, Then steps.' },
      summary: { name: 'Summary', hint: 'Sum it up in a few sentences.' },
      'line-breaks': { name: 'Fix line breaks', hint: 'Join lines broken mid-sentence.' },
      simplify: { name: 'Plain language', hint: 'Use short sentences and everyday words.' },
      plain: { name: 'Plain text', hint: 'Remove bullets, headings, and Markdown.' },
    },
    yours: 'Your tools',
    /** The menu's search, which also takes what to do to the text, run just this once. */
    searchOrSay: 'Search, or say what to do',
    once: 'Just this once',
    onceHint: 'Do this to the text, without making a tool',
    /** What's typed, as a tool's name, such as "Formatting with your instruction…". */
    onceName: 'your instruction',
    loading: 'Loading your tools…',
    noneYet: 'None yet. Make one for a format you use often.',
    newTool: 'New tool',
    manage: 'Manage tools',
    running: (name: string) => `Formatting with ${name}…`,
    applied: (name: string) => `Formatted with ${name}. You can undo this change.`,
    draftChanged: 'Your draft changed while the tool worked. Use it again.',
    /** The dialog where writers keep their tools, built-in and their own. */
    title: 'Manage AI tools',
    empty: 'No tools of your own yet. Make one for each format you use often.',
    confirmDelete: (name: string) => `Delete ${name}?`,
    whatItDoes: 'What it does',
    /** What each built-in tool does, in full; a duplicate starts with it as its instruction. */
    about: {
      bullets:
        'Turns the text into a bulleted list, one point per item. A short lead-in line stays where the text has one.',
      numbered:
        'Lays the text out as numbered steps in the order they happen, one action to each step.',
      checklist:
        'Turns the text into a checklist, one task to each item, starting with what to do. Who and when stay where the text says them.',
      gherkin:
        'Writes the text as Gherkin, for behavior-driven tests: a feature, then a scenario for each behavior, with Given, When, and Then steps. The keywords stay in English; the steps keep the text’s language.',
      summary:
        'Sums the text up in one short paragraph of two to four sentences: its main point, its conclusions, and the names, figures, and dates they depend on. It adds nothing the text doesn’t say.',
      'line-breaks':
        'Joins lines that break in the middle of a sentence, as text copied from a PDF or an email has them, keeping paragraphs and list items apart. Every word stays.',
      simplify:
        'Puts the text in plain language: short sentences, everyday words in place of jargon, and the active voice. Every fact, name, and number stays.',
      plain:
        'Removes the formatting: bullets, numbering, headings, tables, and Markdown become plain sentences and paragraphs, keeping their words.',
    },
    saved: (name: string) => `${name} is saved. Find it under AI tools.`,
    limit: (max: number) => `You can make up to ${String(max)}. Delete one to make another.`,
    name: 'Name',
    namePlaceholder: 'Meeting notes',
    nameMissing: 'Give the tool a name.',
    nameTaken: (name: string) => `You already have a tool called ${name}.`,
    instruction: 'What it does to the text',
    instructionPlaceholder:
      'Lay it out as meeting notes: decisions first, then action items with who does each.',
    instructionMissing: 'Say what the tool does to the text.',
    save: 'Save tool',
    saving: 'Saving…',
    pitch:
      'Make AI tools of your own, such as one that lays out meeting notes, and use them on any draft. Sign in to make yours.',
    notFound: 'That tool no longer exists. Choose another from AI tools.',
    limitReached: 'You have as many tools as you can make. Delete one to make another.',
  },
  /**
   * Messages for failed requests. The API explains problems in English, so English shows its
   * words; other languages say what went wrong from the error's code.
   */
  apiKeys: {
    title: 'API keys',
    intro:
      'Call the Lyrmo API from your own code with a key. Its requests use this account’s plan and daily credits.',
    guide: 'API guide',
    loading: 'Loading your keys…',
    empty: 'No keys yet.',
    name: 'Name',
    namePlaceholder: 'What it’s for, such as Blog publisher',
    create: 'Create key',
    creating: 'Creating…',
    createdNote: 'Your new key. Copy it now and keep it secret: it won’t be shown again.',
    copy: 'Copy key',
    copied: 'Copied',
    createdOn: (date: string) => `Created ${date}`,
    lastUsed: (date: string) => `Last used ${date}`,
    neverUsed: 'Not used yet',
    revoke: 'Revoke',
    /** The Revoke button's name, so each key's can be told apart. */
    revokeKey: (name: string) => `Revoke ${name}`,
    confirmRevoke: (name: string) => `Revoke ${name}? Anything using it stops working at once.`,
    limit: (max: number) =>
      `An account can have ${String(max)} keys. Revoke one to create another.`,
    pitch:
      'Sign in to make API keys, and use Lyrmo from your own code with your plan’s daily credits.',
  },
  errors: {
    generic: 'Something went wrong. Please try again.',
    network: 'Couldn’t reach Lyrmo. Check your connection and try again.',
    unavailable: 'Lyrmo is having trouble right now. Please try again shortly.',
    rateLimited: 'Too many requests. Please wait a moment and try again.',
    rateLimitedFor: (time: string) => `Too many requests. Please try again in ${time}.`,
    creditLimit: 'Not enough credits left today for this request.',
    invalidCode: 'That code is wrong or no longer works. Check the latest email, or use its link.',
    signInRequired: 'Sign in to continue.',
    invalidRequest: 'Something in the request wasn’t right. Check it and try again.',
    tooLong: 'This text is too long.',
    refused: 'This text couldn’t be rewritten. Try different wording.',
    alreadySubscribed: 'You already have a subscription. Choose another plan to switch to it.',
    notSubscribed: 'You don’t have a subscription yet.',
    notFound: 'That couldn’t be found.',
    toneNotFound: 'That tone no longer exists. Choose another from Tone.',
    toneLimit: 'You have as many tones as you can. Delete one to make another.',
    subscriptionChangeRefused:
      'Your subscription couldn’t be changed right now. Please try again later.',
    instructionNotFound:
      'One of the chosen instructions no longer exists. Choose again from Instruction.',
    instructionLimit: 'You have as many instructions as you can save. Delete one to save another.',
    draftNotFound: 'That draft no longer exists. It may have been deleted.',
    draftLimit: 'You have as many drafts as an account can keep. Delete one to save another.',
  },
};

export type Messages = typeof en;
